import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { MdModeEdit, MdSave } from 'react-icons/md';

import useAdminMemberEvent from '../../hooks/useAdminMemberEvent';
import useLoading from '../../hooks/useLoading';
import useCreateVirtualEventUrl from '../../hooks/useCreateVirtualEventUrl';
import useUpdateVirtualEventUrl from '../../hooks/useUpdateVirtualEventUrl';

import Head from '../common/Head';
import AdminTheme from '../theme/AdminTheme';
import Wrapper from '../common/Wrapper';
import Button from '../common/Button';
import {
  HeadingContainer,
  Heading,
  MaxWidthContainer,
  ArchivedSpan,
} from '../common/elements';
import { AdminInputWrapper } from '../common/admin/common';
import { StandaloneAdminTextInput } from '../common/admin/AdminTextInput';
import MemberEventGuestManager from './MemberEventGuestManager';
import MemberEventStatusBadge from './MemberEventStatusBadge';
import {
  $medium,
} from '../../styles';

const MemberEventDetailsContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailsContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: ${$medium};
  margin-top: -0.5rem;
`;

const DetailDivider = styled.div`
  margin: 0 0.5rem;
`;

const UrlInputContainer = styled.div`
  font-weight: ${$medium};
  margin-top: 1rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  > div:first-child {
    margin-right: 0.5rem;
    white-space: nowrap;
  }
  > div:nth-child(2) {
    display: flex;
    align-items: center;
  }
  ${AdminInputWrapper} {
    flex: 1;
  }
`;

const EditButton = styled.div`
  padding: 0 0.5rem;
  margin-left: 0.25rem;
  cursor: pointer;
`;

const StyledEditIcon = styled(MdModeEdit)`
  font-size: 1rem;
`;

const StyledSaveIcon = styled(MdSave)`
  font-size: 1.25rem;
`;

const MemberEventUrlEditor = (props) => {
  const { virtualEventUrl, memberEventId, refetch } = props;
  const [newVirtualEventUrl, setNewVirtualEventUrl] = useState(
    virtualEventUrl && virtualEventUrl.url ? virtualEventUrl.url : '',
  );
  const [showUrlEditor, setShowUrlEditor] = useState(false);
  const [createVirtualEventUrl, { loading: creating }] = useCreateVirtualEventUrl(() => {
    refetch();
    setShowUrlEditor(false);
  });
  const [updateVirtualEventUrl, { loading: updating }] = useUpdateVirtualEventUrl(() => {
    refetch();
    setShowUrlEditor(false);
  });

  const onSave = async () => {
    let url = null;
    if (newVirtualEventUrl.trim().length > 0) {
      url = newVirtualEventUrl.trim();
    }
    if (virtualEventUrl) {
      await updateVirtualEventUrl({
        id: virtualEventUrl.id,
        url,
      });
    } else {
      await createVirtualEventUrl({
        url,
        memberEventId,
      });
    }
  };

  const urlDisplay = (
    <div>
      {
        virtualEventUrl && virtualEventUrl.url ? (
          <a href={virtualEventUrl.url} target="_blank" rel="noreferrer">
            { virtualEventUrl.url }
          </a>
        ) : 'N/A'
      }
      <EditButton onClick={() => setShowUrlEditor(true)}>
        <StyledEditIcon />
      </EditButton>
    </div>
  );

  return (
    <UrlInputContainer>
      <div>Virtual event URL:</div>
      {
        showUrlEditor ? (
          <>
            <StandaloneAdminTextInput
              value={newVirtualEventUrl}
              placeholder="E.g. 'https://zoom.us/j/913456142593'"
              onChange={(e) => setNewVirtualEventUrl(e.target.value)}
              disabled={creating || updating}
            />
            <EditButton onClick={onSave}>
              <StyledSaveIcon />
            </EditButton>
          </>
        ) : urlDisplay
      }
    </UrlInputContainer>
  );
};

MemberEventUrlEditor.propTypes = {
  virtualEventUrl: PropTypes.shape({
    id: PropTypes.number,
    url: PropTypes.string,
  }),
  memberEventId: PropTypes.number.isRequired,
  refetch: PropTypes.func.isRequired,
};

MemberEventUrlEditor.defaultProps = {
  virtualEventUrl: null,
};

const MemberEventDetails = ({ memberEvent, refetch }) => {
  const {
    id,
    name,
    utcDate,
    timezone,
    isVirtual,
    venueByVenueId,
    deletedAt,
    virtualEventUrl,
    status,
  } = memberEvent;

  const formattedDt = moment.tz(utcDate, timezone).format('MMM Do, h:mma z');

  let formattedVenueString;
  if (isVirtual) {
    formattedVenueString = 'Virtual event';
  } else if (venueByVenueId) {
    formattedVenueString = `${venueByVenueId.name}, ${venueByVenueId.cityName}`;
  }

  return (
    <MemberEventDetailsContainer>
      <HeadingContainer>
        <Heading margin="0 auto 0 0">
          { name }
          { deletedAt && <ArchivedSpan>&nbsp;(Archived)</ArchivedSpan> }
        </Heading>
        <MemberEventStatusBadge status={status} margin="0 1rem 0 auto" />
        <Button to={`/admin/member-events/${id}/edit`}>EDIT EVENT</Button>
      </HeadingContainer>
      <DetailsContainer>
        <div>{ formattedDt }</div>
        <DetailDivider>·</DetailDivider>
        <div>{ formattedVenueString }</div>
      </DetailsContainer>
      {
        isVirtual && (
          <MemberEventUrlEditor
            virtualEventUrl={virtualEventUrl}
            refetch={refetch}
            memberEventId={id}
          />
        )
      }
    </MemberEventDetailsContainer>
  );
};

MemberEventDetails.propTypes = {
  memberEvent: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    utcDate: PropTypes.string,
    timezone: PropTypes.string,
    isVirtual: PropTypes.bool,
    venueByVenueId: PropTypes.shape({
      name: PropTypes.string,
      cityName: PropTypes.string,
    }),
    deletedAt: PropTypes.string,
    virtualEventUrl: PropTypes.shape({
      id: PropTypes.number,
      url: PropTypes.string,
    }),
    status: PropTypes.string,
  }).isRequired,
  refetch: PropTypes.func.isRequired,
};

const MemberEventManager = (props) => {
  const { match: { params } } = props;
  const { memberEventId } = params;

  // Load & ingest member event data
  const {
    data,
    loading,
    refetch,
  } = useAdminMemberEvent(memberEventId);

  useLoading([loading]);

  let title = 'Manage Member Event';
  if (data) {
    title = `Manage "${data.name}"`;
  }

  return (
    <AdminTheme>
      <Head title={title} />
      <Wrapper>
        <MaxWidthContainer maxWidth="56rem">
          {
            !loading && (
              <>
                <MemberEventDetails memberEvent={data} refetch={refetch} />
                <MemberEventGuestManager memberEvent={data} />
              </>
            )
          }
        </MaxWidthContainer>
      </Wrapper>
    </AdminTheme>
  );
};

MemberEventManager.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      memberEventId: PropTypes.string,
    }),
  }).isRequired,
};

export default MemberEventManager;
